import * as GC from '@mescius/spread-sheets';
import type { LoopRange } from './loop';
import {
  ACTIVE_LOOP_COLOR,
  ACTIVE_LOOP_HEADER_COLOR,
  EXCEL_FONT,
} from '../constants';

export function applyCellBackground(
  sheet: GC.Spread.Sheets.Worksheet,
  row: number,
  col: number,
  color: string | GC.Spread.Sheets.IPatternFill
): void {
  sheet.getCell(row, col).backColor(color);
}

export function clearCellBackground(
  sheet: GC.Spread.Sheets.Worksheet,
  row: number,
  col: number
): void {
  sheet.getCell(row, col).backColor('');
}

export function clearBackgroundsInLoop(
  sheet: GC.Spread.Sheets.Worksheet,
  loop: LoopRange
): void {
  for (let row = loop.startRow; row <= loop.endRow; row++) {
    for (let col = loop.startCol; col <= loop.endCol; col++) {
      clearCellBackground(sheet, row, col);
    }
  }
}

export function applyLoopBackground(
  sheet: GC.Spread.Sheets.Worksheet,
  loop: Pick<LoopRange, 'startRow' | 'startCol' | 'endRow' | 'endCol'>
): void {
  sheet
    .getRange(
      loop.startRow,
      loop.startCol,
      loop.endRow - loop.startRow + 1,
      loop.endCol - loop.startCol + 1
    )
    .backColor({
      type: GC.Spread.Sheets.PatternType.darkDown,
      backgroundColor: ACTIVE_LOOP_COLOR,
      patternColor: 'white',
    });
}

export function applyLoopHeaderBackground(
  sheet: GC.Spread.Sheets.Worksheet,
  loop: Pick<
    LoopRange,
    'startRow' | 'startCol' | 'headerEndRow' | 'headerEndCol'
  >
): void {
  if (loop.headerEndCol === undefined || loop.headerEndRow === undefined) {
    const err = new Error('no header in loop');
    console.log(err);
    throw err;
  }

  sheet
    .getRange(
      loop.startRow,
      loop.startCol,
      loop.headerEndRow - loop.startRow + 1,
      loop.headerEndCol - loop.startCol + 1
    )
    .backColor({
      type: GC.Spread.Sheets.PatternType.darkDown,
      backgroundColor: ACTIVE_LOOP_HEADER_COLOR,
      patternColor: 'white',
    });
}

// style.fontの文字サイズを保持したままフォント名だけ変換
function replaceFontFamilyKeepSize(fontStr: string, newFamily: string) {
  // 1) 先頭に "xxpx" または "xxpt" が来るケース
  const m1 = fontStr.match(/^\s*([\d.]+(?:px|pt))\s+(.+)\s*$/i);
  if (m1) {
    const size = m1[1];
    return `${size} ${newFamily}`;
  }

  // 2) "normal normal bold 12pt/normal Arial" のようなフル表現
  const idx = fontStr.lastIndexOf(' ');
  if (idx !== -1) {
    return fontStr.substring(0, idx + 1) + newFamily;
  }

  // 3) どれにも当てはまらない場合はそのまま返す
  return fontStr;
}

// cellRange内のフォントが利用可能なフォント以外の場合はデフォルトのフォントに置き換える
export function applyDefaultFont(
  sheet: GC.Spread.Sheets.Worksheet,
  cellRange: GC.Spread.Sheets.Range
): void {
  const allowed = Object.keys(EXCEL_FONT).map(
    (key) => EXCEL_FONT[key as keyof typeof EXCEL_FONT]
  );

  sheet.suspendPaint();

  for (let r = cellRange.row; r < cellRange.row + cellRange.rowCount; r++) {
    for (let c = cellRange.col; c < cellRange.col + cellRange.colCount; c++) {
      let cell = sheet.getRange(r, c, 1, 1);
      let currentFont = cell.font();
      if (!currentFont) continue;

      if (!allowed.some((a) => currentFont.includes(a))) {
        let newFont = replaceFontFamilyKeepSize(
          currentFont,
          EXCEL_FONT['MS_GOTHIC']
        );
        cell.font(newFont);
      }
    }
  }

  sheet.resumePaint();
}
